import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, ChevronLeft, ChevronRight, Users, ShoppingBag, Euro, Percent } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPartnerSpending = () => {
  const navigate = useNavigate();
  const [spending, setSpending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [editingCommission, setEditingCommission] = useState(null);
  const [commissionValue, setCommissionValue] = useState("");

  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const monthNames = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

  const formatMonth = (monthStr) => {
    const [y, m] = monthStr.split("-");
    return `${monthNames[parseInt(m) - 1]} ${y}`;
  };

  const changeMonth = (delta) => {
    const [y, m] = currentMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const fetchSpending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/partner-spending?month=${currentMonth}`, { headers });
      setSpending(res.data);
    } catch {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { fetchSpending(); }, [fetchSpending]);

  const saveCommission = async (companyId) => {
    const val = parseFloat(commissionValue);
    if (isNaN(val) || val < 0 || val > 100) {
      toast.error("Inserisci un valore tra 0 e 100");
      return;
    }
    try {
      await axios.patch(`${API}/api/admin/partner-companies/${companyId}/commission`,
        { commission_percentage: val }, { headers });
      toast.success("Commissione aggiornata");
      setEditingCommission(null);
      fetchSpending();
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const totals = spending.reduce((acc, s) => ({
    orders: acc.orders + s.num_orders,
    spent: acc.spent + s.total_spent,
    commission: acc.commission + s.commission_amount
  }), { orders: 0, spent: 0, commission: 0 });

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-partner-spending">
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Building2 className="w-6 h-6 text-blue-400" />
          <h1 className="text-xl font-bold">Spesa Aziende Partner</h1>
        </div>

        {/* Month Selector */}
        <div className="flex items-center justify-center gap-4 mb-6" data-testid="month-selector">
          <button onClick={() => changeMonth(-1)} className="p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-lg font-semibold min-w-[180px] text-center">{formatMonth(currentMonth)}</span>
          <button onClick={() => changeMonth(1)} className="p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
            <ShoppingBag className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-2xl font-bold">{totals.orders}</p>
            <p className="text-xs text-zinc-500">Ordini totali</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
            <Euro className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
            <p className="text-2xl font-bold">€{totals.spent.toFixed(2)}</p>
            <p className="text-xs text-zinc-500">Spesa totale</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
            <Percent className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-2xl font-bold">€{totals.commission.toFixed(2)}</p>
            <p className="text-xs text-zinc-500">Da riconoscere</p>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Caricamento...</div>
        ) : spending.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">Nessuna azienda partner configurata</div>
        ) : (
          <div className="space-y-3">
            {spending.map((s) => (
              <div key={s.company_id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid={`partner-row-${s.company_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-white text-lg">{s.company_name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-zinc-400">
                      <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{s.users_count} dipendenti</span>
                      <span className="inline-flex items-center gap-1"><ShoppingBag className="w-3.5 h-3.5" />{s.num_orders} ordini</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-400">€{s.total_spent.toFixed(2)}</p>
                    <p className="text-xs text-zinc-500">speso nel mese</p>
                  </div>
                </div>

                {/* Commission Row */}
                <div className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-zinc-300">Riconoscimento:</span>
                    {editingCommission === s.company_id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0" max="100" step="0.5"
                          value={commissionValue}
                          onChange={(e) => setCommissionValue(e.target.value)}
                          className="w-20 bg-black border border-zinc-600 rounded px-2 py-1 text-sm text-white"
                          data-testid="commission-input"
                          autoFocus
                        />
                        <span className="text-sm text-zinc-400">%</span>
                        <button onClick={() => saveCommission(s.company_id)}
                          className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700" data-testid="save-commission-btn">
                          Salva
                        </button>
                        <button onClick={() => setEditingCommission(null)}
                          className="text-xs bg-zinc-700 text-white px-2 py-1 rounded hover:bg-zinc-600">
                          Annulla
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingCommission(s.company_id); setCommissionValue(String(s.commission_percentage)); }}
                        className="text-sm font-semibold text-blue-400 hover:text-blue-300 underline"
                        data-testid="edit-commission-btn"
                      >
                        {s.commission_percentage}%
                      </button>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-yellow-400">€{s.commission_amount.toFixed(2)}</span>
                    <span className="text-xs text-zinc-500 ml-1">da riconoscere</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartnerSpending;
